/**
 * Portal Init Selectors
 * Memoized selectors for portal initialization state
 */

import { createSelector } from '@ufc/core'
import type {
  PortalInitState,
  InitData,
  InitType,
  UserProfile,
  TenantSummary,
  CustomerAccountSummary,
  UserSettings,
  PermissionsMatrix,
  FeatureFlags,
  PricingProfile,
  PriceListSummary,
  RegionSummary,
  CitySummary,
} from '../domain/types'
import {
  isGuestInit,
  isEcommerceInit,
  isCustomerInit,
  isAdminInit,
  isAuthenticatedInit,
} from '../domain/types'

// ============================================================================
// Base Selector
// ============================================================================

type StateWithInit = { portalInit: PortalInitState }

export const selectInitState = (state: StateWithInit): PortalInitState => state.portalInit

// ============================================================================
// Status Selectors
// ============================================================================

export const selectInitStatus = createSelector(
  selectInitState,
  (init) => init.status
)

export const selectInitError = createSelector(
  selectInitState,
  (init) => init.error
)

export const selectIsInitialized = createSelector(
  selectInitStatus,
  (status) => status === 'success'
)

export const selectIsInitializing = createSelector(
  selectInitStatus,
  (status) => status === 'loading'
)

export const selectInitFailed = createSelector(
  selectInitStatus,
  (status) => status === 'error'
)

export const selectLastInitialized = createSelector(
  selectInitState,
  (init) => init.lastInitialized
)

// ============================================================================
// Data Selectors
// ============================================================================

export const selectInitData = createSelector(
  selectInitState,
  (init): InitData | null => init.data
)

export const selectInitType = createSelector(
  selectInitData,
  (data): InitType | null => data?.type ?? null
)

// Type check selectors
export const selectIsGuest = createSelector(
  selectInitData,
  (data): boolean => isGuestInit(data)
)

export const selectIsEcommerceUser = createSelector(
  selectInitData,
  (data): boolean => isEcommerceInit(data)
)

export const selectIsCustomerUser = createSelector(
  selectInitData,
  (data): boolean => isCustomerInit(data)
)

export const selectIsAdminUser = createSelector(
  selectInitData,
  (data): boolean => isAdminInit(data)
)

export const selectIsAuthenticated = createSelector(
  selectInitData,
  (data): boolean => isAuthenticatedInit(data)
)

// ============================================================================
// Profile Selectors (works for authenticated users only)
// ============================================================================

export const selectProfile = createSelector(
  selectInitData,
  (data): UserProfile | null => {
    if (data && isAuthenticatedInit(data)) {
      return data.profile
    }
    return null
  }
)

export const selectProfileEmail = createSelector(
  selectProfile,
  (profile) => profile?.email ?? null
)

export const selectProfileUsername = createSelector(
  selectProfile,
  (profile) => profile?.username ?? null
)

export const selectProfileTenant = createSelector(
  selectProfile,
  (profile): TenantSummary | null => profile?.tenant ?? null
)

export const selectProfileRole = createSelector(
  selectProfile,
  (profile) => profile?.role ?? null
)

export const selectProfileRoleType = createSelector(
  selectProfileRole,
  (role) => role?.type ?? null
)

// ============================================================================
// Feature Flags Selector (works for all types)
// ============================================================================

export const selectFeatureFlags = createSelector(
  selectInitData,
  (data): FeatureFlags | null => data?.featureFlags ?? null
)

export const selectEnableBetaFeatures = createSelector(
  selectFeatureFlags,
  (flags) => flags?.enableBetaFeatures ?? false
)

export const selectEnableAnalytics = createSelector(
  selectFeatureFlags,
  (flags) => flags?.enableAnalytics ?? true
)

export const selectEnableCopilot = createSelector(
  selectFeatureFlags,
  (flags) => flags?.enableCopilot ?? false
)

// ============================================================================
// Ecommerce Selectors (guest or ecommerce user)
// ============================================================================

export const selectPricingProfile = createSelector(
  selectInitData,
  (data): PricingProfile | null => {
    if (data && isEcommerceInit(data)) {
      return data.pricingProfile
    }
    return null
  }
)

export const selectPriceList = createSelector(
  selectInitData,
  (data): PriceListSummary | null => {
    if (isGuestInit(data)) {
      return data.ecommerce.priceList
    }
    if (isEcommerceInit(data)) {
      return data.pricingProfile?.priceList ?? null
    }
    return null
  }
)

export const selectCustomerGroup = createSelector(
  selectPricingProfile,
  (profile) => profile?.customerGroup ?? null
)

// ============================================================================
// Customer-specific Selectors
// ============================================================================

export const selectCustomerAccount = createSelector(
  selectInitData,
  (data): CustomerAccountSummary | null => {
    if (data && isCustomerInit(data)) {
      return data.customerAccount
    }
    return null
  }
)

export const selectAllowedRegions = createSelector(
  selectCustomerAccount,
  (account): RegionSummary[] => account?.allowedRegions ?? []
)

export const selectAllowedRegionIds = createSelector(
  selectAllowedRegions,
  (regions) => regions.map(region => String(region.id ?? region.documentId))
)

// City-based access control (NEW - primary method)
export const selectAllowedCities = createSelector(
  selectCustomerAccount,
  (account): CitySummary[] => account?.allowedCities ?? []
)

export const selectAllowedCityIds = createSelector(
  selectAllowedCities,
  (cities) => cities.map(city => String(city.id ?? city.documentId))
)

export const selectAllowedCityDocumentIds = createSelector(
  selectAllowedCities,
  (cities) => cities.map(city => city.documentId)
)

export const selectCreditLimit = createSelector(
  selectCustomerAccount,
  (account) => account?.creditLimit ?? 0
)

export const selectPaymentTerms = createSelector(
  selectCustomerAccount,
  (account) => account?.paymentTerms ?? null
)

export const selectAccountStatus = createSelector(
  selectCustomerAccount,
  (account) => account?.accountStatus ?? null
)

export const selectUserSettings = createSelector(
  selectInitData,
  (data): UserSettings | null => {
    if (data && isCustomerInit(data)) {
      return data.settings
    }
    return null
  }
)

export const selectUserLanguage = createSelector(
  selectUserSettings,
  (settings) => settings?.language ?? 'en'
)

export const selectUserTimezone = createSelector(
  selectUserSettings,
  (settings) => settings?.timezone ?? 'Asia/Riyadh'
)

export const selectNotificationSettings = createSelector(
  selectUserSettings,
  (settings) => settings?.notifications ?? { email: true, push: false, sms: false }
)

// ============================================================================
// Admin-specific Selectors
// ============================================================================

export const selectPermissions = createSelector(
  selectInitData,
  (data): PermissionsMatrix | null => {
    if (data && isAdminInit(data)) {
      return data.permissions
    }
    return null
  }
)

// Permission boolean selectors
export const selectCanManageTenants = createSelector(
  selectPermissions,
  (permissions) => permissions?.canManageTenants ?? false
)

export const selectCanManageUsers = createSelector(
  selectPermissions,
  (permissions) => permissions?.canManageUsers ?? false
)

export const selectCanManageCustomerAccounts = createSelector(
  selectPermissions,
  (permissions) => permissions?.canManageCustomerAccounts ?? false
)

export const selectCanViewAllOrders = createSelector(
  selectPermissions,
  (permissions) => permissions?.canViewAllOrders ?? false
)

export const selectCanApproveOrders = createSelector(
  selectPermissions,
  (permissions) => permissions?.canApproveOrders ?? false
)

export const selectCanManageFleet = createSelector(
  selectPermissions,
  (permissions) => permissions?.canManageFleet ?? false
)

export const selectCanViewReports = createSelector(
  selectPermissions,
  (permissions) => permissions?.canViewReports ?? false
)

export const selectDetailedPermissions = createSelector(
  selectPermissions,
  (permissions) => permissions?.detailed ?? []
)

// Legacy selectors for backwards compatibility
export const selectIsCustomerInit = selectIsCustomerUser
export const selectIsAdminInit = selectIsAdminUser
